// (c) chaehongee. Confidential and proprietary.

import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

export interface ReleaseInfo {
  version: string
  title: string
  date: string
  summary?: string
}

interface Manifest {
  versions: ReleaseInfo[]
}

const DISMISSED_VERSION_KEY = 'whats_new_dismissed_version'
const SESSION_CLOSED_KEY = 'whats_new_session_closed'

async function fetchManifest(lang: string): Promise<Manifest> {
  if (lang !== 'en') {
    const res = await fetch(`/whatsnew/${lang}/manifest.json`)
    if (res.ok) return res.json() as Promise<Manifest>
  }
  const res = await fetch('/whatsnew/manifest.json')
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<Manifest>
}

/**
 * Hook to manage the "What's New" popup state.
 * Shows the popup when a logged-in user visits and hasn't seen the latest version.
 *
 * @param enabled - Only activate when user is logged in
 */
export function useWhatsNew(enabled: boolean) {
  const { i18n } = useTranslation()
  const [versions, setVersions] = useState<ReleaseInfo[]>([])
  const [isOpen, setIsOpen] = useState(false)

  const lang = i18n.language?.split('-')[0] ?? 'en'

  useEffect(() => {
    if (!enabled) return

    fetchManifest(lang)
      .then((data: Manifest) => {
        if (!data.versions?.length) return
        setVersions(data.versions)

        const latestVersion = data.versions[0].version

        // Don't show if permanently dismissed for this version
        if (localStorage.getItem(DISMISSED_VERSION_KEY) === latestVersion) return

        // Don't show if closed this browser session
        if (sessionStorage.getItem(SESSION_CLOSED_KEY) === latestVersion) return

        setIsOpen(true)
      })
      .catch(() => {
        // Silently fail — manifest not found is fine
      })
  }, [enabled, lang])

  const close = useCallback(() => {
    if (versions.length > 0) {
      sessionStorage.setItem(SESSION_CLOSED_KEY, versions[0].version)
    }
    setIsOpen(false)
  }, [versions])

  const dismiss = useCallback(() => {
    if (versions.length > 0) {
      localStorage.setItem(DISMISSED_VERSION_KEY, versions[0].version)
    }
    setIsOpen(false)
  }, [versions])

  return { isOpen, versions, close, dismiss }
}
